(() => {
  'use strict';
  if (customElements.get('doi-voice-input')) return;
  const config = document.querySelector('[data-doi-voice-labels]');
  if (!config) return;
  const labels = JSON.parse(config.textContent);
  const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  let active = null;

  class VoiceInput extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.hidden = true;
      if (!Recognition || !window.isSecureContext) return;
      this.input = this.closest('form')?.querySelector('#' + CSS.escape(this.getAttribute('for') || ''));
      const template = document.querySelector('[data-doi-voice-icon]');
      if (!this.input || !template) return;
      this.events = new AbortController();
      const on = (target, name, fn, options = {}) => target?.addEventListener(name, fn, { ...options, signal: this.events.signal });
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'doi-voice-button';
      this.button.setAttribute('aria-label', labels.search);
      this.button.setAttribute('aria-pressed', 'false');
      this.button.append(template.content.cloneNode(true));
      this.replaceChildren(this.button);
      this.notice = document.createElement('div');
      this.notice.className = 'doi-voice-notice';
      this.notice.hidden = true;
      this.notice.setAttribute('role', 'status');
      this.notice.setAttribute('aria-live', 'polite');
      this.notice.setAttribute('aria-atomic', 'true');
      document.body.append(this.notice);
      this.hidden = false;
      on(this.button, 'click', () => this.session ? this.stop() : this.start());
      on(this.button, 'pointerenter', () => { if (!this.session) this.show(labels.search); });
      on(this.button, 'pointerleave', () => { if (!this.session && document.activeElement !== this.button) this.hide(); });
      on(this.button, 'focus', () => { if (!this.session) this.show(labels.search); });
      on(this.button, 'blur', () => { if (!this.session) this.hide(); });
      on(document, 'keydown', event => {
        if (this.session && event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.cancel(true);
        }
      }, { capture: true });
      on(document, 'pointerdown', event => {
        if (this.session && !this.contains(event.target)) this.cancel(false);
      }, { capture: true });
      on(document, 'focusin', event => {
        if (this.session && !this.closest('form')?.contains(event.target)) this.cancel(false);
      });
      on(this.input, 'beforeinput', () => this.cancel(false));
      on(this.input, 'input', () => { if (this.session) this.cancel(false); });
      on(this.closest('form'), 'submit', () => this.cancel(false), { capture: true });
      on(this.closest('doi-live-search,doi-page-search'), 'doi:close-search', () => this.cancel(false));
      on(this.closest('doi-search-page') || document.querySelector('doi-search-page'), 'doi:query', () => this.cancel(false));
      on(document, 'visibilitychange', () => { if (document.hidden) this.cancel(false); });
      on(window, 'pagehide', () => this.cancel(false));
      on(window, 'popstate', () => this.cancel(false));
      on(window, 'scroll', () => this.position(), { capture: true, passive: true });
      on(window, 'resize', () => this.position(), { passive: true });
      on(window.visualViewport, 'resize', () => this.position(), { passive: true });
      on(window.visualViewport, 'scroll', () => this.position(), { passive: true });
    }

    disconnectedCallback() {
      this.cancel(false);
      this.events?.abort();
      this.events = null;
      clearTimeout(this.noticeTimer);
      this.notice?.remove();
    }

    available() {
      return this.isConnected && this.input?.isConnected && !this.input.disabled && !this.input.readOnly &&
        !this.closest('[hidden],[aria-hidden="true"]') &&
        this.button.getClientRects().length > 0 && getComputedStyle(this.button).visibility !== 'hidden';
    }

    show(message, timeout = 0) {
      clearTimeout(this.noticeTimer);
      this.notice.textContent = message;
      this.notice.hidden = false;
      this.position();
      if (timeout) this.noticeTimer = setTimeout(() => this.hide(), timeout);
    }

    hide() {
      if (this.notice) this.notice.hidden = true;
    }

    position() {
      if (!this.notice || this.notice.hidden) return;
      const rect = this.button.getBoundingClientRect();
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0, top = viewport?.offsetTop || 0;
      const width = viewport?.width || window.innerWidth, height = viewport?.height || window.innerHeight;
      this.notice.style.maxWidth = Math.max(0, Math.min(320, width - 24)) + 'px';
      const box = this.notice.getBoundingClientRect();
      this.notice.style.left = Math.max(left + 12, Math.min(rect.right - box.width, left + width - box.width - 12)) + 'px';
      this.notice.style.top = Math.max(top + 12, Math.min(rect.bottom + 12, top + height - box.height - 12)) + 'px';
    }

    reset(session) {
      clearTimeout(session.timer);
      clearInterval(session.watch);
      if (this.session === session) this.session = null;
      if (active === this) active = null;
      this.removeAttribute('data-listening');
      this.button.setAttribute('aria-pressed', 'false');
      this.button.setAttribute('aria-label', labels.search);
      this.button.removeAttribute('aria-busy');
    }

    cancel(focus) {
      const session = this.session;
      if (!session) return;
      this.reset(session);
      try { session.recognition.abort(); } catch {}
      this.hide();
      if (focus && this.input.isConnected) this.input.focus({ preventScroll: true });
    }

    stop() {
      const session = this.session;
      if (!session || session.stopping) return;
      session.stopping = true;
      this.button.setAttribute('aria-busy', 'true');
      this.show(labels.finishing);
      clearTimeout(session.timer);
      session.timer = setTimeout(() => this.finish(session), 3000);
      try { session.recognition.stop(); } catch { this.finish(session); }
    }

    finish(session) {
      if (this.session !== session) return;
      const usable = this.available() && this.input.value === session.base;
      this.reset(session);
      try { session.recognition.abort(); } catch {}
      if (!usable) { this.hide(); return; }
      const transcript = session.final.trim();
      if (!transcript) { this.show(labels.no_speech, 6000); return; }
      const before = session.base.slice(0, session.start), after = session.base.slice(session.end);
      const insertion = (before && !/\s$/.test(before) ? ' ' : '') + transcript + (after && !/^\s/.test(after) ? ' ' : '');
      const value = before + insertion + after;
      const limit = this.input.maxLength > 0 ? this.input.maxLength : 2000;
      this.input.value = value.slice(0, limit);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.input.focus({ preventScroll: true });
      const caret = Math.min(before.length + insertion.length, limit);
      this.input.setSelectionRange(caret, caret);
      this.show(value.length > limit ? labels.limit : labels.added, 5000);
    }

    start() {
      if (!this.available()) return;
      active?.cancel(false);
      let recognition;
      try { recognition = new Recognition(); } catch { this.show(labels.unavailable, 6000); return; }
      const session = {
        recognition, base: this.input.value, start: this.input.selectionStart ?? this.input.value.length,
        end: this.input.selectionEnd ?? this.input.value.length, final: '', stopping: false
      };
      this.session = session;
      active = this;
      recognition.lang = document.documentElement.lang || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      this.setAttribute('data-listening', '');
      this.button.setAttribute('aria-label', labels.stop);
      this.button.setAttribute('aria-pressed', 'true');
      this.show(labels.starting);
      recognition.onstart = () => { if (this.session === session && !session.stopping) this.show(labels.listening + ' ' + labels.privacy); };
      recognition.onresult = event => {
        if (this.session !== session) return;
        let final = '', interim = '';
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) final += result[0].transcript + ' ';
          else interim += result[0].transcript + ' ';
        }
        session.final = final;
        const heard = (final + interim).trim().slice(0, 240);
        this.show(heard ? labels.heard.replace('[text]', heard) : labels.listening);
      };
      recognition.onend = () => this.finish(session);
      recognition.onerror = event => {
        if (this.session !== session) return;
        this.reset(session);
        try { recognition.abort(); } catch {}
        const message = ['not-allowed', 'service-not-allowed'].includes(event.error) ? labels.denied :
          event.error === 'audio-capture' ? labels.no_microphone : event.error === 'no-speech' ? labels.no_speech : labels.unavailable;
        this.show(message, 7000);
      };
      session.timer = setTimeout(() => this.stop(), 30000);
      session.watch = setInterval(() => { if (!this.available()) this.cancel(false); }, 250);
      try { recognition.start(); } catch { this.reset(session); this.show(labels.unavailable, 6000); }
    }
  }

  customElements.define('doi-voice-input', VoiceInput);
})();
